import { useState, useEffect, useCallback } from 'react';
import { onAuthChange, checkIsAdmin, ensureAnonymousAuth } from '../services/auth';
import { getScoringSession, saveScoringSession } from '../utils/scoringSession';
import { TOURNAMENT_ID } from '../config/app';
import { joinGroup } from '../services/functions';
import type { AuthState } from '../types';

export function useAuth(): AuthState & { signOut: () => Promise<void> } {
  const [state, setState] = useState<AuthState>({
    uid: null,
    isAdmin: false,
    isGlobalAdmin: false,
    isTournamentAdmin: false,
    isAnonymous: false,
    loading: true,
  });

  useEffect(() => {
    const unsubscribe = onAuthChange(async (user) => {
      if (!user) {
        // Trigger silent anonymous sign-in
        try {
          await ensureAnonymousAuth();
        } catch {
          setState({
            uid: null,
            isAdmin: false,
            isGlobalAdmin: false,
            isTournamentAdmin: false,
            isAnonymous: false,
            loading: false,
          });
        }
        return;
      }

      const isAnon = user.isAnonymous;
      let adminStatus = {
        isAdmin: false,
        isGlobalAdmin: false,
        isTournamentAdmin: false,
      };

      if (!isAnon) {
        try {
          adminStatus = await checkIsAdmin(user.uid);
        } catch {
          adminStatus = {
            isAdmin: false,
            isGlobalAdmin: false,
            isTournamentAdmin: false,
          };
        }
      }

      setState({
        uid: user.uid,
        isAdmin: adminStatus.isAdmin,
        isGlobalAdmin: adminStatus.isGlobalAdmin,
        isTournamentAdmin: adminStatus.isTournamentAdmin,
        isAnonymous: isAnon,
        loading: false,
      });
    });

    return unsubscribe;
  }, []);

  const signOut = useCallback(async () => {
    // Capture the scoring session before the auth session changes
    const session = getScoringSession();

    const { signOut: doSignOut } = await import('../services/auth');
    await doSignOut();

    // Re-establish anonymous auth — this creates a brand-new anonymous UID
    await ensureAnonymousAuth();

    // If the user had an active scoring session, silently rejoin the group with
    // the new anonymous UID so that Firestore write permissions are restored.
    // joinGroup is idempotent and safe to call even if this UID is already a member.
    if (session?.roundId && session?.groupId) {
      try {
        // Use the join code already stored in the session, or fall back to
        // reading it from the Firestore group document (for sessions created
        // before the joinCode was persisted to localStorage).
        let joinCode = session.joinCode;
        if (!joinCode) {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('../services/firebase');
          const groupSnap = await getDoc(
            doc(db, 'tournaments', TOURNAMENT_ID, 'rounds', session.roundId, 'groups', session.groupId),
          );
          joinCode = groupSnap.data()?.joinCode as string | undefined;
          // Cache it so future sign-outs don't need the extra Firestore read
          if (joinCode) {
            saveScoringSession({ ...session, joinCode });
          }
        }

        if (joinCode) {
          await joinGroup({ roundId: session.roundId, joinCode });
        }
      } catch (err) {
        // Rejoin failed — log for debugging. The session stays in localStorage so
        // the "Continue Scoring" link remains and HoleScoring still allows entry
        // via the localStorage-based isMember check. The session is only cleared
        // by FinaliseFlow when the group is successfully finalised.
        console.error('[signOut] Auto-rejoin failed:', err);
      }
    }
  }, []);

  return { ...state, signOut };
}
